import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { CreatePollInput, Poll, PollOption } from '../types';

/**
 * Hook to create polls
 */
export const useCreatePoll = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Create a new poll with message
   */
  const createPoll = useCallback(
    async (input: CreatePollInput, userId: string): Promise<string | null> => {
      if (!input.question.trim()) {
        setError('Vraag is verplicht');
        return null;
      }

      if (input.options.length < 2) {
        setError('Minimaal 2 opties zijn verplicht');
        return null;
      }

      // Filter out empty options
      const validOptions = input.options.filter((opt) => opt.trim());
      if (validOptions.length < 2) {
        setError('Minimaal 2 opties zijn verplicht');
        return null;
      }

      try {
        setLoading(true);
        setError(null);

        // 1. Create the message first
        const { data: messageData, error: messageError } = await supabase
          .from('messages')
          .insert({
            chat_id: input.chat_id,
            sender_id: userId,
            content: `📊 ${input.question}`,
            type: 'poll',
            metadata: {
              poll_question: input.question,
              is_anonymous: input.is_anonymous || false,
              allows_multiple: input.allows_multiple_answers || false,
            },
          })
          .select('id')
          .single();

        if (messageError) throw messageError;

        // 2. Create the poll
        const { data: pollData, error: pollError } = await supabase
          .from('polls')
          .insert({
            message_id: messageData.id,
            chat_id: input.chat_id,
            question: input.question.trim(),
            is_anonymous: input.is_anonymous || false,
            allows_multiple_answers: input.allows_multiple_answers || false,
            closes_at: input.closes_at?.toISOString() || null,
            created_by: userId,
          })
          .select('id')
          .single();

        if (pollError) throw pollError;

        // 3. Create poll options
        const optionsToInsert = validOptions.map((text, index) => ({
          poll_id: pollData.id,
          option_text: text.trim(),
          option_order: index,
        }));

        const { error: optionsError } = await supabase
          .from('poll_options')
          .insert(optionsToInsert);

        if (optionsError) throw optionsError;

        // 4. Update the message with the poll_id in metadata
        await supabase
          .from('messages')
          .update({
            metadata: {
              poll_id: pollData.id,
              poll_question: input.question,
              is_anonymous: input.is_anonymous || false,
              allows_multiple: input.allows_multiple_answers || false,
            },
          })
          .eq('id', messageData.id);

        return pollData.id;
      } catch (err) {
        console.error('Error creating poll:', err);
        setError(err instanceof Error ? err.message : 'Kon poll niet aanmaken');
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return {
    createPoll,
    loading,
    error,
    clearError: () => setError(null),
  };
};

